import { useCallback, useRef, useState } from 'react'
import { apiPost } from '../../generic/Api_Functions'
import { claimFreshIds, createQuerySequence, isBatchAnswer, launchQuery, requestedIds, upsertRequestedEntries } from './requestedEntries'

/**
 * Resolves the main-image data for a set of part references with batch calls (anti-N+1, CA17 / S50),
 * so a list never issues one request per row. Two batch endpoints are used per query:
 *  - `partReferences/mainImages`: metadata `{ hasMainImage, documentId, uuid, fileName, extension }`,
 *    keyed by part reference id — needed by the enlarge viewer (uuid/fileName/extension).
 *  - `partReferences/mainImages/thumbnails`: the list thumbnail itself as base64 JPEG, keyed by id.
 *    Only references with a thumbnail appear; a missing id means "no image" (placeholder).
 *
 * The frontend always resolves from the part reference id (S49) rather than from data copied into the
 * line/OF/order, so the view reflects the current main image after a refresh (S51 / S55).
 *
 * The lists query for many reasons — the page refresh, the "N items selected" counter (which asks
 * only for the selected rows), the edit preview, closing the document manager — and every query
 * follows the same rules (story #163):
 *  - it only updates the ids it asked for: present in the answer → overwritten, absent → removed
 *    (placeholder); every other known entry is kept (RN1, RN2);
 *  - both maps are updated from the same query, or neither is (a thumbnail never comes with stale
 *    viewer data);
 *  - the query launched later wins, per id: a slow answer never overwrites a newer one (RN6);
 *  - a failed query, or one without ids, changes nothing and an empty one issues no request: rows
 *    keep their last known thumbnail, the table keeps working (RN7, RN8, D3);
 *  - a map keeps its object identity when nothing changes in it (RN9, #2819 — requestedEntries.js).
 *
 * @returns {[Object, (ids: Array<number|string>) => Promise<void>, (ids: Array<number|string>) => Promise<void>, Object]}
 *   A tuple: the metadata map keyed by id, the resolver used by the list `dataFetch`, the resolver
 *   used for a single reference (edit preview, document manager) — both with the same semantics —
 *   and the thumbnail map (id → base64 JPEG).
 */
const useMainImageResolution = () => {
  const [mainImageInfoMap, setMainImageInfoMap] = useState({})
  const [mainImageThumbnailMap, setMainImageThumbnailMap] = useState({})
  // RN6 — the query launched later wins, per id (see requestedEntries.js).
  const querySequence = useRef(createQuerySequence())

  const resolveMainImages = useCallback(async (partReferenceIds) => {
    // Deduplicate before requesting: several rows can reference the same part reference.
    const ids = requestedIds(partReferenceIds)
    if (ids.length === 0) {
      return
    }
    const queryNumber = launchQuery(querySequence.current)
    try {
      // One batch call each (metadata + thumbnails) per query; never one request per row.
      const [infoMap, thumbnailMap] = await Promise.all([
        apiPost('partReferences/mainImages', ids),
        apiPost('partReferences/mainImages/thumbnails', ids)
      ])
      if (!isBatchAnswer(infoMap) || !isBatchAnswer(thumbnailMap)) {
        return
      }
      const freshIds = claimFreshIds(querySequence.current, ids, queryNumber)
      setMainImageInfoMap((previous) => upsertRequestedEntries(previous, freshIds, infoMap))
      setMainImageThumbnailMap((previous) => upsertRequestedEntries(previous, freshIds, thumbnailMap))
    } catch (error) {
      // Non-blocking and silent: rows keep their last known thumbnail; the next refresh retries.
    }
  }, [])

  // Kept as a separate element of the tuple so the consumers stay untouched: resolving a single
  // reference (edit preview, document manager) is just another query, sharing the same sequence so a
  // preview launched after a refresh wins over it (RN11).
  const mergeMainImages = resolveMainImages

  return [mainImageInfoMap, resolveMainImages, mergeMainImages, mainImageThumbnailMap]
}

export default useMainImageResolution
